import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { Base64 } from 'js-base64';
//src/app/core/services/user.service.ts
import { UserService } from '../../core/services/user.service';
import { createEventData, download } from '../../utils/event-download.utils';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfigService } from 'src/app/initializer/config.service';
import * as ics from 'ics';
import { BreakPointService } from '../../shared/services/break-point.service';
import { UpcomingDeadlineItem } from '../new-reports/report-statuses/models/upcoming-deadline-item.model';
import { UpcomingDeadlinesService } from '../new-reports/report-statuses/services/upcoming-deadlines.service';
import { FundTypeFormat } from '../new-reports/report-statuses/services/deadline-rules.model';
import { TeamService } from '../team/team.service';
import { TeamMember } from '../team/team-member.model';
import { environment } from 'src/environments/environment';
import { ManagementService } from '../management/management.service';
import { ITaskApi } from '../management/task-management.models';
import { TaskDetailsComponent } from '../management/management.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DashboardDataService } from './dashboard-data.service';
import { normalizeLanguage } from 'src/app/utils/language.util';
import {
  DashboardActivityItem,
  DashboardCalendarTask,
  GpDashboardData,
  GpDashboardDecision,
} from './dashboard-data.models';

@Component({
  selector: 'app-dash',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly gpDashboardCancel$ = new Subject<void>();
  private readonly dashboardActivityCancel$ = new Subject<void>();
  private readonly dashboardVehicleCancel$ = new Subject<void>();
  private gpDashboardRequestSequence = 0;
  private dashboardVehicleRequestSequence = 0;
  private dashboardActivityRequestSequence = 0;
  private taskDetailsDialogRef?: MatDialogRef<TaskDetailsComponent>;
  selected = false;
  languageCode = '';
  gpDashboardData: GpDashboardData | null = null;
  isGpDashboardLoading = false;
  gpDashboardError = false;
  dashboardEventData: any = {};
  dashboardNewsData: any = {};
  dashboardTaskData: DashboardCalendarTask[] = [];
  dashboardUpdateData: any[] = [];
  dashboardActivityData: DashboardActivityItem[] = [];
  filteredUpdates: any[] = [];
  selectedGP: any = {};
  selectedVehicle: any = {};
  userName = '';
  isEventLoading!: boolean;
  isNewsLoading!: boolean;
  isTaskLoading = true;
  isActivityLoading = false;
  isEventError = false;
  isNewsError = false;
  isTaskError = false;
  isUpdateError = false;
  isActivityError = false;
  isUpdateloaded!: boolean;
  upcomingDeadlines: UpcomingDeadlineItem[] = [];

  showTeamWidget = !!environment.teamApiUrl;
  teamMembers: TeamMember[] = [];
  activeTaskCountByMember: Record<string, number> = {};
  isActiveTaskCountsLoading = false;
  isTeamLoading = false;
  isTeamError = false;
  readonly teamWidgetLimit = 4;
  // hideCapitalAccount: boolean = true;

  ngOnInit(): void {
    const userConfig = this.userService.getUserConfigObject();
    this.languageCode = normalizeLanguage(userConfig.languageCode);
    /* Added this code for getting username to display in dashboard header, as per the new design. */
    this.userName = (userConfig.name || '')
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')[0];

    this.activatedRoute.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const gpId = params.get('gpId');
        const vehicleId = params.get('vehicleId');
        if (!gpId || !vehicleId) {
          return;
        }

        this.loadDashboardForVehicle(gpId, vehicleId);
      });

    this.userService.selectedVehicleObservable$
      .pipe(takeUntil(this.destroy$))
      .subscribe((vehicle: any) => {
        if (!vehicle || typeof vehicle === 'string') {
          return;
        }
        this.refreshUpcomingDeadlines(
          vehicle?.type as FundTypeFormat | undefined
        );
      });

    this.managementService.taskCreatedObservable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const vehicleId =
          this.selectedVehicle?.id ||
          this.activatedRoute.snapshot.params['vehicleId'];

        if (!vehicleId) {
          return;
        }

        this.getDashboardTaskData(
          vehicleId,
          this.dashboardVehicleRequestSequence
        );
      });

    this.managementService.taskListItemUpdatedObservable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ task }) => {
        this.applyTaskListItemUpdate(task);
      });
  }

  private loadDashboardForVehicle(gpId: string, vehicleId: string): void {
    const requestSequence = ++this.dashboardVehicleRequestSequence;
    this.dashboardVehicleCancel$.next();
    this.dashboardActivityCancel$.next();
    this.gpDashboardData = null;
    this.gpDashboardError = false;
    this.dashboardEventData = [];
    this.dashboardNewsData = {};
    this.dashboardTaskData = [];
    this.dashboardUpdateData = [];
    this.dashboardActivityData = [];
    this.activeTaskCountByMember = {};
    this.isActiveTaskCountsLoading = false;
    this.showTeamWidget = !!environment.teamApiUrl;
    this.teamMembers = [];
    this.isTeamLoading = false;
    this.isTeamError = false;
    this.isEventError = false;
    this.isNewsError = false;
    this.isTaskError = false;
    this.isUpdateError = false;
    this.isActivityError = false;
    this.filteredUpdates = [];
    this.isGpDashboardLoading = true;
    this.isEventLoading = false;
    this.isNewsLoading = false;
    this.isTaskLoading = false;
    this.isActivityLoading = false;
    this.isUpdateloaded = false;
    const baseconfigData = this.config.baseData.data;
    this.selectedGP = baseconfigData.gps.find((item: any) => item.id === gpId);
    this.selectedVehicle = this.selectedGP?.vehicles.find(
      (item: any) => item.id === vehicleId
    );

    // Team members must be loaded before the rest of the dashboard so that
    // opening a task from the calendar always has the full assignee list
    // available (mirrors the Management tab's load-team-then-tasks order).
    const loadRestOfDashboard = () => {
      if (requestSequence !== this.dashboardVehicleRequestSequence) {
        return;
      }
      this.getGpDashboardData(vehicleId, requestSequence);
      this.getDashboardCalendarEvents(vehicleId, requestSequence);
      this.getDashboardNewsData(vehicleId, requestSequence);
      this.getDashboardTaskData(vehicleId, requestSequence);
      this.getDashboardUpdateData(vehicleId, requestSequence);
      this.refreshUpcomingDeadlines(
        this.selectedVehicle?.type as FundTypeFormat | undefined
      );
    };

    // Always fetch the firm team (mirrors the Management tab), regardless of
    // whether the Team widget itself is shown: the assignee dropdown for
    // tasks opened from the calendar depends on the full team list too.
    this.getTeamMembers(loadRestOfDashboard);
  }

  private refreshUpcomingDeadlines(fundType?: FundTypeFormat): void {
    this.upcomingDeadlines =
      this.upcomingDeadlinesService.getUpcoming(fundType);
  }

  constructor(
    private userService: UserService,
    public datepipe: DatePipe,
    private router: Router,
    private translate: TranslateService,
    private config: ConfigService,
    private activatedRoute: ActivatedRoute,
    public breakPointService: BreakPointService,
    private readonly upcomingDeadlinesService: UpcomingDeadlinesService,
    private teamService: TeamService,
    private managementService: ManagementService,
    private dashboardDataService: DashboardDataService,
    private dialog: MatDialog
  ) {}

  getTeamMembers(onComplete?: () => void) {
    // `teamApiUrl` only controls whether the Team widget UI is shown; the
    // firm team itself must still be fetched (e.g. for assignee dropdowns
    // on tasks opened from the calendar) regardless of that flag.
    this.showTeamWidget = !!environment.teamApiUrl;
    this.isTeamLoading = true;
    this.isTeamError = false;
    this.teamService
      .getFirmTeam()
      .pipe(takeUntil(this.dashboardVehicleCancel$), takeUntil(this.destroy$))
      .subscribe({
        next: members => {
          this.teamMembers = members ?? [];
        },
        error: () => {
          this.isTeamError = true;
          this.isTeamLoading = false;
          onComplete?.();
        },
        complete: () => {
          this.isTeamLoading = false;
          onComplete?.();
        },
      });
  }

  activeTaskLabel(member: TeamMember): string {
    if (this.isActiveTaskCountsLoading) {
      return 'Loading active tasks';
    }
    if (this.isTaskError) {
      return 'Unavailable';
    }
    const count = this.activeTaskCountByMember[member.id] ?? 0;
    return `${count} active task${count === 1 ? '' : 's'}`;
  }

  goToTeam() {
    const gpId = this.activatedRoute.snapshot.params['gpId'];
    const vehicleId = this.activatedRoute.snapshot.params['vehicleId'];
    this.router.navigate([`/gp/${gpId}/vehicle/${vehicleId}/team`]);
  }

  openTeammate(member: TeamMember) {
    const gpId = this.activatedRoute.snapshot.params['gpId'];
    const vehicleId = this.activatedRoute.snapshot.params['vehicleId'];
    this.router.navigate(
      [`/gp/${gpId}/vehicle/${vehicleId}/team/${member.id}`],
      { state: { member } }
    );
  }
  getGpDashboardData(
    vehicleId: string,
    vehicleRequestSequence = this.dashboardVehicleRequestSequence
  ): void {
    const requestSequence = ++this.gpDashboardRequestSequence;
    this.gpDashboardCancel$.next();
    if (!environment.teamApiUrl || !vehicleId) {
      this.gpDashboardData = null;
      this.gpDashboardError = true;
      this.isGpDashboardLoading = false;
      return;
    }

    this.isGpDashboardLoading = true;
    this.gpDashboardError = false;
    this.dashboardDataService
      .getFundDashboard(vehicleId, this.selectedGP?.id, {
        tenantId: this.selectedVehicle?.tenantId,
        gpTenantId: this.selectedGP?.tenantId,
      })
      .pipe(
        takeUntil(this.gpDashboardCancel$),
        takeUntil(this.destroy$),
        finalize(() => {
          if (
            requestSequence !== this.gpDashboardRequestSequence ||
            vehicleRequestSequence !== this.dashboardVehicleRequestSequence
          ) {
            return;
          }
          if (this.isGpDashboardLoading && !this.gpDashboardData) {
            this.isGpDashboardLoading = false;
            this.gpDashboardError = true;
          }
        })
      )
      .subscribe({
        next: response => {
          if (
            requestSequence !== this.gpDashboardRequestSequence ||
            vehicleRequestSequence !== this.dashboardVehicleRequestSequence
          ) {
            return;
          }
          this.gpDashboardData = response.dashboard;
          this.isGpDashboardLoading = false;
          this.getDashboardActivityData(
            response.fundId,
            vehicleRequestSequence
          );
        },
        error: () => {
          if (
            requestSequence !== this.gpDashboardRequestSequence ||
            vehicleRequestSequence !== this.dashboardVehicleRequestSequence
          ) {
            return;
          }
          this.gpDashboardData = null;
          this.gpDashboardError = true;
          this.isGpDashboardLoading = false;
        },
      });
  }

  getDashboardActivityData(
    fundId: string,
    vehicleRequestSequence = this.dashboardVehicleRequestSequence
  ): void {
    const activityRequestSequence = ++this.dashboardActivityRequestSequence;
    this.dashboardActivityCancel$.next();
    this.isActivityLoading = true;
    this.isActivityError = false;
    this.dashboardDataService
      .getFundActivity(fundId)
      .pipe(takeUntil(this.dashboardActivityCancel$), takeUntil(this.destroy$))
      .subscribe({
        next: activityItems => {
          if (
            activityRequestSequence !== this.dashboardActivityRequestSequence ||
            vehicleRequestSequence !== this.dashboardVehicleRequestSequence
          ) {
            return;
          }
          this.dashboardActivityData = activityItems;
          this.isActivityError = false;
          this.isActivityLoading = false;
        },
        error: () => {
          if (
            activityRequestSequence !== this.dashboardActivityRequestSequence ||
            vehicleRequestSequence !== this.dashboardVehicleRequestSequence
          ) {
            return;
          }
          this.dashboardActivityData = [];
          this.isActivityError = true;
          this.isActivityLoading = false;
        },
      });
  }

  retryFundActivity(fundId: string): void {
    this.getDashboardActivityData(fundId);
  }

  formatDashboardMoney(
    value: string | number | null | undefined,
    currency = this.gpDashboardData?.fund.currency
  ): string {
    if (value === null || value === undefined || value === '') return '—';
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '—';
    const display = this.resolveCurrencyDisplay(currency);
    const absolute = Math.abs(numeric);
    if (absolute >= 1_000_000)
      return `${display}${(numeric / 1_000_000).toFixed(1)}m`;
    if (absolute >= 1_000) return `${display}${(numeric / 1_000).toFixed(0)}k`;
    return `${display}${numeric.toLocaleString('en-US', {
      maximumFractionDigits: 0,
    })}`;
  }

  formatDashboardDate(value: string | null | undefined): string {
    if (!value) return '—';
    return this.datepipe.transform(value, 'dd MMM yyyy') || '—';
  }

  formatPerformancePeriod(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return `Q${
      Math.floor(date.getUTCMonth() / 3) + 1
    } ${date.getUTCFullYear()}`;
  }

  collectionRate(dashboard: GpDashboardData): string {
    const called = Number(dashboard.capitalActivity.totalIssuedAmount);
    const outstanding = Number(dashboard.capitalActivity.outstandingAmount);
    if (
      !Number.isFinite(called) ||
      called <= 0 ||
      !Number.isFinite(outstanding)
    ) {
      return '—';
    }
    return `${Math.max(
      0,
      Math.min(100, Math.round(((called - outstanding) / called) * 100))
    )}%`;
  }

  hasIssuedCapitalActivity(dashboard: GpDashboardData): boolean {
    const totalIssued = Number(dashboard.capitalActivity.totalIssuedAmount);
    return Number.isFinite(totalIssued) && totalIssued > 0;
  }

  performanceTrendPoints(): string {
    return this.performanceChartPoints(100, 100, 0, 0, 12, 16);
  }

  performanceSparklinePoints(): string {
    return this.performanceChartPoints(180, 32, 4, 4, 4, 4);
  }

  private performanceChartPoints(
    width: number,
    height: number,
    left: number,
    right: number,
    top: number,
    bottom: number
  ): string {
    const trend = (this.gpDashboardData?.performance.trend ?? []).filter(
      point => Number.isFinite(Number(point.value))
    );
    if (!trend.length) return '';
    const values = trend.map(point => Number(point.value));
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || Math.max(Math.abs(max) * 0.1, 1);
    const chartWidth = width - left - right;
    const chartHeight = height - top - bottom;
    return trend
      .map((point, index) => {
        const value = Number(point.value);
        const x =
          trend.length === 1
            ? width / 2
            : left + (index / (trend.length - 1)) * chartWidth;
        const y = bottom + ((max - value) / range) * chartHeight;
        return `${x.toFixed(2)},${Math.max(
          top,
          Math.min(height - bottom, y)
        ).toFixed(2)}`;
      })
      .join(' ');
  }

  performanceAreaPoints(): string {
    const line = this.performanceTrendPoints();
    return line ? `0,92 ${line} 100,92` : '';
  }

  formatDecisionState(state: string): string {
    return state
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/^\w/, char => char.toUpperCase());
  }

  formatDecisionMetadata(metadata: string | null | undefined): string {
    if (!metadata) return 'Review needed';

    const dueMatch = metadata.match(/^Due (.+)$/);
    if (dueMatch) {
      return `Due ${this.formatDashboardDate(dueMatch[1])}`;
    }

    const outstandingMatch = metadata.match(
      /^(\d+) investors · ([\d.,]+) outstanding$/
    );
    if (outstandingMatch) {
      return `${outstandingMatch[1]} investors · ${this.formatDashboardMoney(
        outstandingMatch[2]
      )} outstanding`;
    }

    return metadata;
  }

  formatFeedStatus(status: string | null | undefined): string {
    if (!status) return '';
    return status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/^\w/, char => char.toUpperCase());
  }

  performanceAxisLabels(): string[] {
    const values = (this.gpDashboardData?.performance.trend ?? [])
      .map(point => Number(point.value))
      .filter(value => Number.isFinite(value));
    if (!values.length) return [];

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || Math.max(Math.abs(max) * 0.1, 1);
    return [max, min + range * 0.67, min + range * 0.33, min].map(value =>
      this.formatChartAxisValue(value, range)
    );
  }

  private formatChartAxisValue(value: number, range: number): string {
    const absolute = Math.abs(value);
    if (absolute >= 1_000_000) {
      const precision = range < 5_000_000 ? 1 : 0;
      return `${(value / 1_000_000).toFixed(precision)}m`;
    }
    if (absolute >= 1_000) {
      const precision = range < 5_000 ? 1 : 0;
      return `${(value / 1_000).toFixed(precision)}k`;
    }
    return Math.round(value).toLocaleString('en-US');
  }

  decisionIcon(type: string): string {
    const icons: Record<string, string> = {
      capitalCall: 'payments',
      onboarding: 'group_add',
      task: 'task_alt',
      request: 'rate_review',
      notification: 'notifications',
      chat: 'chat_bubble_outline',
    };
    return icons[type] ?? 'arrow_forward';
  }

  isDashboardDecisionNavigable(decision: GpDashboardDecision): boolean {
    return ['onboarding', 'task', 'request', 'capitalCall', 'chat'].includes(
      decision.type
    );
  }

  openDashboardDecision(decision: GpDashboardDecision): void {
    if (!this.isDashboardDecisionNavigable(decision)) {
      return;
    }
    const gpId = this.activatedRoute.snapshot.params['gpId'];
    const vehicleId = this.activatedRoute.snapshot.params['vehicleId'];
    if (decision.type === 'onboarding') {
      this.router.navigate([`/gp/${gpId}/vehicle/${vehicleId}/onboarding`]);
      return;
    }
    if (decision.type === 'task' || decision.type === 'request') {
      this.router.navigate([`/gp/${gpId}/vehicle/${vehicleId}/management`]);
      return;
    }
    if (decision.type === 'capitalCall') {
      this.router.navigate([
        `/gp/${gpId}/vehicle/${vehicleId}/transactions/capital-calls`,
      ]);
      return;
    }
    if (decision.type === 'chat') {
      this.router.navigate([`/gp/${gpId}/vehicle/${vehicleId}/chat`]);
    }
  }
  ngOnDestroy(): void {
    this.taskDetailsDialogRef?.close();
    this.dashboardVehicleCancel$.next();
    this.dashboardVehicleCancel$.complete();
    this.gpDashboardCancel$.next();
    this.gpDashboardCancel$.complete();
    this.dashboardActivityCancel$.next();
    this.dashboardActivityCancel$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resolveCurrencyDisplay(currencyCode?: string | null): string {
    if (!currencyCode) {
      return '';
    }

    try {
      const symbol = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
      })
        .formatToParts(1)
        .find(part => part.type === 'currency')?.value;

      return symbol || currencyCode;
    } catch {
      return currencyCode;
    }
  }

  private getDashboardCalendarEvents(
    vehicleId: string,
    vehicleRequestSequence = this.dashboardVehicleRequestSequence
  ): void {
    this.isEventLoading = false;
    this.isEventError = false;
    this.dashboardDataService
      .getCalendarEvents(vehicleId, this.selectedGP?.id, {
        tenantId: this.selectedVehicle?.tenantId,
        gpTenantId: this.selectedGP?.tenantId,
      })
      .pipe(takeUntil(this.dashboardVehicleCancel$), takeUntil(this.destroy$))
      .subscribe({
        next: events => {
          if (vehicleRequestSequence !== this.dashboardVehicleRequestSequence) {
            return;
          }
          this.dashboardEventData = events.map(event => ({
            id: event.id,
            title: event.title,
            description: event.description ?? '',
            start: event.startsAt,
            end: event.endsAt ?? event.startsAt,
            location: event.location ?? '',
          }));
          this.isEventError = false;
          this.isEventLoading = true;
        },
        error: () => {
          if (vehicleRequestSequence !== this.dashboardVehicleRequestSequence) {
            return;
          }
          this.dashboardEventData = [];
          this.isEventError = true;
          this.isEventLoading = true;
        },
      });
  }

  getDashboardNewsData(
    vehicleId: string,
    vehicleRequestSequence = this.dashboardVehicleRequestSequence
  ) {
    this.isNewsLoading = false;
    this.isNewsError = false;
    this.dashboardDataService
      .getNews(vehicleId)
      .pipe(takeUntil(this.dashboardVehicleCancel$), takeUntil(this.destroy$))
      .subscribe(
        response => {
          if (vehicleRequestSequence !== this.dashboardVehicleRequestSequence) {
            return;
          }
          this.dashboardNewsData = response;
          this.isNewsError = false;
          this.isNewsLoading = true;
        },
        () => {
          if (vehicleRequestSequence !== this.dashboardVehicleRequestSequence) {
            return;
          }
          this.dashboardNewsData = [];
          this.isNewsError = true;
          this.isNewsLoading = true;
        }
      );
  }

  getDashboardTaskData(
    vehicleId: string,
    vehicleRequestSequence = this.dashboardVehicleRequestSequence
  ) {
    if (!environment.teamApiUrl) {
      this.dashboardTaskData = [];
      this.activeTaskCountByMember = {};
      this.isActiveTaskCountsLoading = false;
      this.isTaskLoading = true;
      return;
    }

    // Only drop into the loading state when there's nothing to show yet (first
    // load or after an error). A background refresh (e.g. after a task edit)
    // should keep the current list visible instead of flashing the skeleton.
    if (!this.dashboardTaskData.length) {
      this.isTaskLoading = false;
    }
    this.isTaskError = false;
    this.isActiveTaskCountsLoading = true;
    this.dashboardDataService
      .getTasks(vehicleId, this.selectedGP?.id)
      .pipe(takeUntil(this.dashboardVehicleCancel$))
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        taskData => {
          if (vehicleRequestSequence !== this.dashboardVehicleRequestSequence) {
            return;
          }
          this.dashboardTaskData = taskData.calendarTasks;
          this.activeTaskCountByMember = taskData.activeTaskCountByMember;
          this.isActiveTaskCountsLoading = false;
          this.isTaskError = false;
          this.isTaskLoading = true;
        },
        () => {
          if (vehicleRequestSequence !== this.dashboardVehicleRequestSequence) {
            return;
          }
          this.dashboardTaskData = [];
          this.activeTaskCountByMember = {};
          this.isActiveTaskCountsLoading = false;
          this.isTaskError = true;
          this.isTaskLoading = true;
        }
      );
  }

  private applyTaskListItemUpdate(task: ITaskApi): void {
    const isVisibleTask = !!task.dueAt && task.status !== 'Completed';

    this.dashboardTaskData = this.dashboardTaskData
      .map(existingTask =>
        existingTask.id === task.id ? { ...existingTask, ...task } : existingTask
      )
      .filter(existingTask =>
        existingTask.id === task.id ? isVisibleTask : true
      );
  }

  getDashboardUpdateData(
    vehicleId: string,
    vehicleRequestSequence = this.dashboardVehicleRequestSequence
  ) {
    this.isUpdateloaded = false;
    this.isUpdateError = false;
    this.dashboardDataService
      .getUpdates(vehicleId)
      .pipe(takeUntil(this.dashboardVehicleCancel$), takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          if (vehicleRequestSequence !== this.dashboardVehicleRequestSequence) {
            return;
          }
          const parsedData = Array.isArray(response)
            ? JSON.parse(JSON.stringify(response))
            : JSON.parse(JSON.stringify(response?.data ?? []));
          if (Array.isArray(parsedData)) {
            parsedData.forEach((updateItem: any, index: number) => {
              if (updateItem?.context?.documentId) {
                const decodedString = Base64.decode(
                  updateItem.context.documentId
                );
                const splitStr = decodedString?.split('/LPs/');
                if (splitStr.length > 1) {
                  const lpDesignation = splitStr[1]?.split('/')[0];
                  const replacedString = decodedString.replace(
                    `/LPs/${lpDesignation}`,
                    '/Limited Partner'
                  );
                  parsedData[index]['context']['documentId'] =
                    Base64.encode(replacedString);
                }
              }
              // Precompute the translated display title for the calendar widget
              parsedData[index]['displayTitle'] =
                this.getTitlename(updateItem).content;
            });
          }
          this.dashboardUpdateData = parsedData;
          this.filteredUpdates = this.dashboardUpdateData;
          this.isUpdateloaded = true;
          this.isUpdateError = false;
        },
        () => {
          if (vehicleRequestSequence !== this.dashboardVehicleRequestSequence) {
            return;
          }
          this.dashboardUpdateData = [];
          this.filteredUpdates = [];
          this.isUpdateError = true;
          this.isUpdateloaded = true;
        }
      );
  }

  retryCalendarData(): void {
    const vehicleId = this.selectedVehicle?.id;
    if (!vehicleId) {
      return;
    }
    const requestSequence = this.dashboardVehicleRequestSequence;
    this.getDashboardCalendarEvents(vehicleId, requestSequence);
    this.getDashboardNewsData(vehicleId, requestSequence);
    this.getDashboardTaskData(vehicleId, requestSequence);
    this.getDashboardUpdateData(vehicleId, requestSequence);
  }

  private formatTimePeriod(timePeriod?: string): string {
    if (!timePeriod) {
      return '';
    }

    const [year, period] = timePeriod.split('-');

    if (period?.startsWith('q')) {
      return `${period.toUpperCase()} ${year}`;
    }

    if (period?.startsWith('h')) {
      return `${period.toUpperCase()} ${year}`;
    }

    return timePeriod;
  }
  navigateUpdate(update: any) {
    const gpId = this.activatedRoute.snapshot.params['gpId'];
    const vehicleId = this.activatedRoute.snapshot.params['vehicleId'];

    switch (update.type) {
      case 'newQuarterlyReport': {
        return this.router.navigate([
          `/gp/${gpId}/vehicle/${vehicleId}/portfolio-group/new-reports/quarterly`,
        ]);
      }
      default:
        return null;
    }
  }

  goToLink(url: string) {
    window.open(url, '_blank');
  }

  openTask(task: DashboardCalendarTask) {
    if (this.taskDetailsDialogRef) {
      return;
    }

    this.managementService.closeDrawer();
    this.managementService.setTaskDetails({
      title: task.name,
      topic: task.topic,
      taskId: task.id,
      timeStamp: task.dueAt
        ? `Due ${this.datepipe.transform(task.dueAt, 'dd. MMM yyyy')}`
        : task.dueLabel,
      status: task.status,
      vehicleId: this.activatedRoute.snapshot.params['vehicleId'],
      // Initial assignee list for immediate render; TaskDetailsComponent also
      // pulls the cached firm-team stream directly to guarantee the full list
      // even if the Dashboard's teamMembers hadn't finished loading yet.
      assigneeList: this.teamMembers,
      isSuggest: false,
      isDialog: true,
    });

    this.taskDetailsDialogRef = this.dialog.open(TaskDetailsComponent, {
      width: 'min(760px, calc(100vw - 32px))',
      maxWidth: 'calc(100vw - 32px)',
      height: 'min(760px, calc(100vh - 32px))',
      maxHeight: 'calc(100vh - 32px)',
      panelClass: 'task-details-dialog-panel',
      backdropClass: 'create-task-dialog-backdrop',
      ariaLabel: 'Task details',
      autoFocus: 'first-tabbable',
    });
    this.taskDetailsDialogRef.afterClosed().subscribe(() => {
      this.taskDetailsDialogRef = undefined;
      this.managementService.setTaskDetails(null);
    });
  }

  navigateToLPs() {
    const gpId = this.activatedRoute.snapshot.params['gpId'];
    const vehicleId = this.activatedRoute.snapshot.params['vehicleId'];
    this.router.navigate([
      '/gp',
      gpId,
      'vehicle',
      vehicleId,
      'portfolio-group',
      'investors',
    ]);
  }

  iCalDownload(_event: any) {
    const event: any = createEventData(_event);
    if (event) {
      ics.createEvent(event, (error: any, value: any) => {
        if (error) {
          console.log(error);
          return;
        }
        download('InvestorEvent.ics', value);
      });
    }
  }
  //function to check if the event is an all day event
  isAllDayEvent(_event: any): boolean {
    const startDate = new Date(_event.start);
    const hasEndDate = _event.end && _event.end.length > 0;

    if (!hasEndDate) {
      return false;
    }
    const isValidStartTime =
      startDate.getUTCHours() === 0 &&
      startDate.getUTCMinutes() === 0 &&
      startDate.getUTCSeconds() === 0;
    const endDate = new Date(_event.end);
    const isValidEndTime =
      endDate.getUTCHours() === 0 &&
      endDate.getUTCMinutes() === 0 &&
      endDate.getUTCSeconds() === 0;

    return !(isValidStartTime && isValidEndTime);
  }
  getTitlename(data: any) {
    const lpSuffix = data.limitedPartner?.name
      ? ` · ${data.limitedPartner.name}`
      : '';

    switch (data.type) {
      case 'newDocument': {
        return {
          type: 'tiles',
          content: (data.context?.filename ?? data.title) + lpSuffix,
        };
      }
      case 'newQuarterlyReport': {
        return {
          type: 'tiles',
          content: data.title + lpSuffix,
        };
      }
      case 'newCapitalAccountOverview': {
        return {
          type: 'tiles',
          content: `${this.translate.instant(
            'translations.dashboard.updates.capitalAccountOverview'
          )} ${this.formatTimePeriod(data.context?.timePeriod)}${lpSuffix}`,
        };
      }
      case 'newTransaction': {
        return {
          type: 'tiles',
          content:
            (data.context.type === 'capitalCall'
              ? 'New ' +
                this.translate.instant(
                  'translations.dashboard.capital.capitalCall'
                )
              : 'New ' +
                this.translate.instant(
                  'translations.dashboard.capital.cashDistribution'
                )) + lpSuffix,
        };
      }
      case 'dueTransaction': {
        return {
          type: 'tiles',
          content:
            (data.context.type === 'capitalCall'
              ? 'Due ' +
                this.translate.instant(
                  'translations.dashboard.capital.capitalCall'
                )
              : 'Due ' +
                this.translate.instant(
                  'translations.dashboard.capital.cashDistribution'
                )) + lpSuffix,
        };
      }
    }
    return {
      type: 'tiles',
      content: `${data.context?.filename ?? data.title}${lpSuffix}`,
    };
  }

  onUpdatesChange(updateBy: any) {
    return (this.filteredUpdates = this.dashboardUpdateData.filter(
      (data: any) => !!data[updateBy.value]
    ));
  }
}
